import type { Action } from '@automata/wfb-api';
import { createSelector } from '@reduxjs/toolkit';
import { flatMap, mapValues, pickBy, reduce, startCase } from 'lodash';
import type { XYPosition } from 'reactflow';

import type { TaskDependency } from './graph/graphSlice';
import {
  selectDataDependencyEdges,
  selectDataHandleIsConnectedByHandleID,
  selectLabwareDependencyEdges,
  selectNodeIdToColorIndex,
  selectSelectedEdges,
  selectSelectedNodeForPropertiesSidebar,
  selectSelectedTimeConstraintIdForPropertiesSidebar,
  selectTaskDependencies,
  selectTaskDependencyEdges,
  selectTimeConstraintInstances,
} from './graph/selectors';
import { filterCodeTaskDataHandlesByTaskId, toDataHandleId } from './helpers';
import { selectLabwareIdsForTaskByTaskId } from './selectors/selectLabwareIdsForTaskByTaskId';
import { LabwareByHandleIndexByTaskId } from './selectors/types';
import {
  _selectZoom,
  selectFlowPositionCenter,
  selectUserIsDraggingSelectionBox,
} from './ui/selectors';
import {
  selectActionTaskPropertiesStrict,
  selectConditionalTaskPropertiesStrict,
  selectDataExportInstrumentsConfigsById,
  selectDataExportOutputDriversById,
  selectDriverForTaskByTaskId,
  selectLabwareIds,
  selectLabwareProperties,
  selectScripts,
  selectTaskIds,
  selectTaskPropertiesStrict,
} from './workflow/selectors';
import { logger } from '../../../utils';
import {
  LabwareCorrespondenceType,
  ONE_TASK_MISSING_LABWARE,
  SIDEBAR_WIDTH_PX,
} from '../components/constants';
import {
  MIN_GREATER_THAN_MAX_DURATION,
  NO_COMMON_LABWARE,
  TIME_CONSTRAINT_EDGE,
} from '../constants';
import type { RootState } from '../store';
import {
  type AllTaskProperties,
  type DriverByTaskId,
  type GraphEdges,
  type LabwareDataForTask,
  type LabwareDataForTaskByHandlePairId,
  type LabwareProperties,
  PropertiesType,
  type TaskLabwareCorrespondenceById,
  type TaskProperties,
  type TimeConstraintEdge,
  type TimeConstraintErrorType,
  type TimeConstraintErrorsById,
} from '../types';
import { ArgumentAliases, DataHandleComponentData } from '../types/taskNode';
import { getLabwareColorIndex, getTaskLabwareCorrespondence } from '../utils';
import { getParamMode, isConnectorizedInput } from '../utils/dataModes';

export const selectTimeConstraintLabwareCorrespondencesById = createSelector(
  selectLabwareIdsForTaskByTaskId,
  selectTimeConstraintInstances,
  (
    labwareByHandleIndexByTaskId,
    timeConstraintInstances
  ): TaskLabwareCorrespondenceById => {
    const labwareCorrespondences: TaskLabwareCorrespondenceById = {};
    for (const { id, source, target } of Object.values(
      timeConstraintInstances
    )) {
      labwareCorrespondences[id] = getTaskLabwareCorrespondence(
        source,
        target,
        labwareByHandleIndexByTaskId
      );
    }

    return labwareCorrespondences;
  }
);

export const selectTimeConstraintErrorsById = createSelector(
  selectTimeConstraintInstances,
  selectTimeConstraintLabwareCorrespondencesById,
  (
    timeConstraintInstances,
    timeConstraintLabwareCorrespondencesById
  ): TimeConstraintErrorsById => {
    const errors: TimeConstraintErrorsById = {};

    for (const instance of Object.values(timeConstraintInstances)) {
      const { id, minDuration, maxDuration, hardConstraints } = instance;
      const instanceErrors: TimeConstraintErrorType[] = [];

      // skip over time constraints that aren't assigned to labware
      if (
        timeConstraintLabwareCorrespondencesById[id] ===
        LabwareCorrespondenceType.NO_LABWARE_ASSIGNED
      ) {
        continue;
      }

      if (
        timeConstraintLabwareCorrespondencesById[id] ===
        LabwareCorrespondenceType.DIFFERENT_LABWARE
      ) {
        instanceErrors.push(NO_COMMON_LABWARE);
      }

      if (
        minDuration !== null &&
        maxDuration !== null &&
        minDuration > maxDuration
      ) {
        instanceErrors.push(MIN_GREATER_THAN_MAX_DURATION);
      }

      if (hardConstraints && hardConstraints.length > 0) {
        for (const hc of hardConstraints) {
          if (
            hc.minDuration !== null &&
            hc.maxDuration !== null &&
            hc.minDuration > hc.maxDuration
          ) {
            instanceErrors.push(MIN_GREATER_THAN_MAX_DURATION);
          }
        }
      }

      if (instanceErrors.length > 0) {
        errors[id] = instanceErrors;
      }
    }
    return errors;
  }
);

export const selectTimeConstraintEdges = createSelector(
  selectTimeConstraintInstances,
  selectTimeConstraintLabwareCorrespondencesById,
  selectTimeConstraintErrorsById,
  selectSelectedEdges,
  (
    timeConstraintInstances,
    timeConstraintLabwareCorrespondencesById,
    timeConstraintErrorsById,
    selectedEdges
  ): GraphEdges => {
    return Object.values(timeConstraintInstances).map(
      ({
        id,
        source,
        sourceType,
        target,
        targetType,
        minDuration,
        maxDuration,
        hardConstraints,
      }): TimeConstraintEdge => {
        const errors = timeConstraintErrorsById[id];
        const labwareCorrespondence =
          timeConstraintLabwareCorrespondencesById[id] ??
          LabwareCorrespondenceType.NO_LABWARE_ASSIGNED;

        const firstHard =
          hardConstraints && hardConstraints.length > 0
            ? hardConstraints[0]
            : null;
        const visualMin = firstHard ? firstHard.minDuration : minDuration;
        const visualMax = firstHard ? firstHard.maxDuration : maxDuration;

        return {
          id,
          source,
          sourceHandle: sourceType,
          target,
          targetHandle: targetType,
          type: TIME_CONSTRAINT_EDGE,
          selected: selectedEdges.includes(id),
          data: {
            minDuration: visualMin,
            maxDuration: visualMax,
            errors,
            labwareCorrespondence,
          },
        };
      }
    );
  }
);

export const selectAllEdges = createSelector(
  selectLabwareDependencyEdges,
  selectTimeConstraintEdges,
  selectDataDependencyEdges,
  selectTaskDependencyEdges,
  (
    labwareDependencyEdges,
    timeConstraintEdges,
    dataDependencyEdges,
    taskDependencyEdges
  ): GraphEdges => {
    return [
      ...labwareDependencyEdges,
      ...timeConstraintEdges,
      ...dataDependencyEdges,
      ...taskDependencyEdges,
    ];
  }
);

/**
 * Gets information about inputs/outputs for all exportData tasks.
 *
 * @param {Record<string, Driver>} outputDriver - object with driver configurations by taskId (from realDrivers.json).
 * @param {Record<string, AllTaskProperties>} taskProperties - object with all task properties for current workflow.
 * @param {Record<string, boolean>} dataHandleIsConnectedByHandleID - object that defines IsConnectedByHandle for a task.
 *
 * @returns {Record<string, DataHandleComponentData[]>} an object with all inputs/outputs for all exportData tasks.
 */
export const selectDataExportTaskHandlersByTaskID = createSelector(
  selectDataExportOutputDriversById,
  selectTaskPropertiesStrict,
  selectDataHandleIsConnectedByHandleID,
  selectDataExportInstrumentsConfigsById,
  (
    dataExportInstrumentDrivers,
    taskProperties,
    dataHandleIsConnectedByHandleID,
    dataExportInstrumentsConfigs
  ): Record<string, DataHandleComponentData[]> => {
    return mapValues<
      Record<string, AllTaskProperties>,
      DataHandleComponentData[]
    >(taskProperties, (task, taskId) => {
      const argumentAliases: ArgumentAliases = {
        data_key: 'Data',
      };

      // the key by which we will find the required configuration among the instruments
      const dataExportNodeConfigKey = Object.keys(
        dataExportInstrumentsConfigs
      )[0];

      if (
        task.propertiesType === PropertiesType.Action ||
        task.propertiesType === PropertiesType.Code ||
        task.propertiesType === PropertiesType.Conditional ||
        !dataExportNodeConfigKey
      ) {
        return [];
      }

      // list of possible keys by which we will search for arguments for inputs
      const dataExportNodeInputActions =
        dataExportInstrumentsConfigs?.[dataExportNodeConfigKey]?.inputActions;

      // an object with actions that includes arguments
      const actionConfigurationInputObj =
        dataExportInstrumentDrivers?.[dataExportNodeConfigKey]?.actions;

      let dataTaskInputHandles: DataHandleComponentData[] = [];

      if (dataExportNodeInputActions && actionConfigurationInputObj) {
        // the right arguments
        const driversInputArguments = Object.entries(
          actionConfigurationInputObj
        ).find(([key]) => dataExportNodeInputActions.includes(key))?.[1]
          ?.arguments;
        dataTaskInputHandles = reduce(
          driversInputArguments,
          (acc, _, dataKey) => {
            const handleId = toDataHandleId('target', taskId, dataKey);
            const inputObj = {
              name: argumentAliases[dataKey] ?? startCase(dataKey),
              handleType: 'DATA_INPUT',
              handleId,
              dataKey,
              isConnected: dataHandleIsConnectedByHandleID?.[handleId] ?? false,
            } satisfies DataHandleComponentData;
            return [...acc, inputObj];
          },
          [] as DataHandleComponentData[]
        );
      }

      // list of possible keys by which we will search for arguments for outputs
      const dataExportNodeOutputActions =
        dataExportInstrumentsConfigs?.[dataExportNodeConfigKey]?.outputActions;

      // an object with actions that includes arguments
      const actionConfigurationOutputObj =
        dataExportInstrumentDrivers?.[dataExportNodeConfigKey]?.actions;

      let dataTaskOutputHandles: DataHandleComponentData[] = [];

      if (dataExportNodeOutputActions && actionConfigurationOutputObj) {
        // the right arguments
        const driversOutputArguments = Object.entries(
          actionConfigurationOutputObj
        ).find(([key]) => dataExportNodeOutputActions.includes(key))?.[1]
          ?.arguments;

        dataTaskOutputHandles = reduce(
          driversOutputArguments,
          (acc, _, dataKey) => {
            const handleId = toDataHandleId('target', taskId, dataKey);
            const outputObj = {
              name: startCase(dataKey),
              handleType: 'DATA_OUTPUT',
              handleId,
              dataKey,
              isConnected: dataHandleIsConnectedByHandleID?.[handleId] ?? false,
            } satisfies DataHandleComponentData;

            return [...acc, outputObj];
          },
          [] as DataHandleComponentData[]
        );
      }
      return [...dataTaskInputHandles, ...dataTaskOutputHandles];
    });
  }
);

export const selectCodeTaskDataHandlesByTaskID = createSelector(
  selectScripts,
  selectTaskPropertiesStrict,
  selectDataHandleIsConnectedByHandleID,
  (
    scripts,
    taskProperties,
    dataHandleIsConnectedByHandleID
  ): Record<string, DataHandleComponentData[]> => {
    return mapValues<
      Record<string, AllTaskProperties>,
      DataHandleComponentData[]
    >(taskProperties, (task, taskId) => {
      if (task.propertiesType !== PropertiesType.Code) {
        return [];
      }

      const selectedScript = scripts.functions.find(
        (x) => x.function_name === task.scriptName
      );

      const codeTaskInputHandles: DataHandleComponentData[] =
        selectedScript?.arguments
          .filter((arg) => {
            const paramMode = getParamMode(task, arg.name);
            return !paramMode || isConnectorizedInput(task, arg.name);
          })
          .map((arg) => {
            const handleId = toDataHandleId('target', taskId, arg.name);
            return {
              name: startCase(arg.name),
              handleType: 'DATA_INPUT',
              handleId,
              dataKey: arg.name,
              isConnected: dataHandleIsConnectedByHandleID?.[handleId] ?? false,
            } satisfies DataHandleComponentData;
          }) ?? [];

      const codeTaskOutputHandles: DataHandleComponentData[] =
        selectedScript?.returns.map((arg) => {
          const handleId = toDataHandleId('source', taskId, arg.name);
          return {
            dataKey: arg.name,
            handleId,
            handleType: 'DATA_OUTPUT',
            isConnected: dataHandleIsConnectedByHandleID?.[handleId] ?? false,
            name: startCase(arg.name),
            type: arg.type,
          } satisfies DataHandleComponentData;
        }) ?? [];

      return [...codeTaskInputHandles, ...codeTaskOutputHandles];
    });
  }
);
/**
 * @param {Record<string, Driver>} driversForTaskByTaskId - object with driver configurations by taskId (from realDrivers.json).
 * @param {Record<string, AllTaskProperties>} taskPropertiesByTaskId - object with all task properties for current workflow.
 * @param {Record<string, boolean>} dataOutHandlesIsConnectedByTaskID - object that defines IsConnectedByHandle for a task.
 * @returns {Record<string, DataHandleComponentData[]>} an object with all inputs/outputs for all action tasks.
 */
export const selectActionTaskDataHandlesByTaskID = createSelector(
  selectDriverForTaskByTaskId,
  selectActionTaskPropertiesStrict,
  selectDataHandleIsConnectedByHandleID,
  (
    driversForTaskByTaskId,
    taskPropertiesByTaskId,
    dataOutHandlesIsConnectedByTaskID
  ): Record<string, DataHandleComponentData[]> => {
    return mapValues<DriverByTaskId, DataHandleComponentData[]>(
      driversForTaskByTaskId,
      (driver, taskId) => {
        const taskProperties = taskPropertiesByTaskId[taskId] as
          | TaskProperties
          | undefined;

        if (!taskProperties) {
          return [];
        }

        const taskType = taskProperties.taskType;
        const taskDriverActions = driver.actions[taskType] as
          | Action
          | undefined;

        const dataOutputs = pickBy(
          taskDriverActions?.data_outputs,
          (_, key) => {
            if (driver.name !== 'beckman_biomek') return true;

            return (
              key !== 'output_file' ||
              taskProperties.taskParameters?.generate_output_file
            );
          }
        );
        const taskParameters =
          taskProperties.propertiesType === 'action'
            ? taskProperties.taskParameters
            : {};
        const dataInputs = pickBy(
          taskDriverActions?.arguments ?? {},
          (arg, key) =>
            arg.type === 'bytes' || isConnectorizedInput(taskProperties, key)
        );

        const dataInputHandles: DataHandleComponentData[] = Object.entries(
          dataInputs
        ).map(([key]) => {
          const handleId = toDataHandleId('target', taskId, key);
          return {
            name: startCase(key),
            handleType: 'DATA_INPUT',
            handleId,
            dataKey: key,
            isConnected: dataOutHandlesIsConnectedByTaskID?.[handleId] ?? false,
          };
        });

        const dataOutputHandles: DataHandleComponentData[] = Object.values(
          dataOutputs ?? {}
        ).flatMap((dataOutput) => {
          const { suffix_with_field, name } = dataOutput;
          const fieldName = suffix_with_field?.field_name;
          const fieldValue = fieldName && taskParameters?.[fieldName];

          if (
            suffix_with_field &&
            fieldValue &&
            (Array.isArray(fieldValue) || typeof fieldValue === 'string')
          ) {
            const handles = Array.isArray(fieldValue)
              ? fieldValue
              : [fieldValue];
            return handles.map((out) => {
              const handleId = toDataHandleId('source', taskId, out);
              return {
                name: out,
                handleType: 'DATA_OUTPUT',
                handleId,
                dataKey: `${suffix_with_field.field_name}_${out}`,
                isConnected:
                  dataOutHandlesIsConnectedByTaskID?.[handleId] ?? false,
              };
            });
          }

          if (!suffix_with_field) {
            const handleId = toDataHandleId('source', taskId, name);
            return [
              {
                name: startCase(name),
                handleType: 'DATA_OUTPUT',
                handleId,
                dataKey: name,
                isConnected:
                  dataOutHandlesIsConnectedByTaskID?.[handleId] ?? false,
              },
            ];
          }

          return [];
        });

        return [...dataInputHandles, ...dataOutputHandles];
      }
    );
  }
);

export const selectConditionTaskHandlesByTaskID = createSelector(
  selectConditionalTaskPropertiesStrict,
  selectDataHandleIsConnectedByHandleID,
  (
    taskPropertiesByTaskId,
    dataOutHandlesIsConnectedByTaskID
  ): Record<string, DataHandleComponentData[]> => {
    return mapValues<
      Record<string, AllTaskProperties>,
      DataHandleComponentData[]
    >(taskPropertiesByTaskId, (task, taskId) => {
      if (task.propertiesType !== PropertiesType.Conditional) {
        return [];
      }

      const handleId = toDataHandleId('target', taskId, 'source_data');
      const codeTaskInputHandles: DataHandleComponentData[] = [
        {
          name: startCase('source_data'),
          handleType: 'DATA_INPUT',
          handleId,
          dataKey: 'source_data',
          isConnected: dataOutHandlesIsConnectedByTaskID?.[handleId] ?? false,
        } satisfies DataHandleComponentData,
      ];

      const ifHandleId = toDataHandleId('source', taskId, 'if_data');
      const elseHandleId = toDataHandleId('source', taskId, 'else_data');
      const codeTaskOutputHandles: DataHandleComponentData[] = [
        {
          name: task.ifLabel,
          handleType: 'DATA_OUTPUT',
          handleId: ifHandleId,
          dataKey: 'if_data',
          isConnected: dataOutHandlesIsConnectedByTaskID?.[ifHandleId] ?? false,
        } satisfies DataHandleComponentData,
        {
          name: task.elseLabel,
          handleType: 'DATA_OUTPUT',
          handleId: elseHandleId,
          dataKey: 'else_data',
          isConnected:
            dataOutHandlesIsConnectedByTaskID?.[elseHandleId] ?? false,
        } satisfies DataHandleComponentData,
      ];

      return [...codeTaskInputHandles, ...codeTaskOutputHandles];
    });
  }
);

export const selectDataKeyByHandleID = createSelector(
  selectActionTaskDataHandlesByTaskID,
  selectCodeTaskDataHandlesByTaskID,
  selectDataExportTaskHandlersByTaskID,
  selectConditionTaskHandlesByTaskID,
  (
    dataHandlesByTaskID,
    codeTaskDataHandlesByTaskID,
    dataExportTaskDataHandlesByTaskID,
    conditionTaskHandlesByTaskID
  ): Record<string, { dataKey: string; type: string | undefined }> => {
    return reduce(
      flatMap([
        ...Object.values(dataHandlesByTaskID),
        ...Object.values(codeTaskDataHandlesByTaskID),
        ...Object.values(dataExportTaskDataHandlesByTaskID),
        ...Object.values(conditionTaskHandlesByTaskID),
      ]),
      (result, curr) => ({
        ...result,
        [curr.handleId]: { dataKey: curr.dataKey, type: curr.type },
      }),
      {}
    );
  }
);

export const selectDataInputHandlesByTaskID = createSelector(
  selectActionTaskDataHandlesByTaskID,
  (dataHandlesByTaskID) =>
    pickBy(
      mapValues(dataHandlesByTaskID, (dataHandles) =>
        dataHandles.filter(
          (dataHandle) => dataHandle.handleType === 'DATA_INPUT'
        )
      ),
      (array) => array.length > 0
    )
);

export const selectDataOutputHandlesByTaskID = createSelector(
  selectActionTaskDataHandlesByTaskID,
  (dataHandlesByTaskID) =>
    pickBy(
      mapValues(dataHandlesByTaskID, (dataHandles) =>
        dataHandles.filter(
          (dataHandle) => dataHandle.handleType === 'DATA_OUTPUT'
        )
      ),
      (array) => array.length > 0
    )
);

/**
 * Returns the object with arrays of inputs for all exportData tasks.
 */
export const selectDataExportTaskDataInputHandlesByTaskID = createSelector(
  selectDataExportTaskHandlersByTaskID,
  (dataHandlesByTaskID): { [taskId: string]: DataHandleComponentData[] } =>
    filterCodeTaskDataHandlesByTaskId(dataHandlesByTaskID, 'input')
);

/**
 * Returns the object with arrays of outputs for all exportData tasks.
 */
export const selectDataExportTaskOutputHandlesByTaskID = createSelector(
  selectDataExportTaskHandlersByTaskID,
  (dataHandlesByTaskID): { [taskId: string]: DataHandleComponentData[] } =>
    filterCodeTaskDataHandlesByTaskId(dataHandlesByTaskID, 'output')
);

/**
 * Returns the object with arrays of inputs for all processData tasks.
 */
export const selectCodeTaskDataInputHandlesByTaskID = createSelector(
  selectCodeTaskDataHandlesByTaskID,
  (dataHandlesByTaskID): { [taskId: string]: DataHandleComponentData[] } =>
    filterCodeTaskDataHandlesByTaskId(dataHandlesByTaskID, 'input')
);

/**
 * Returns the object with arrays of outputs for all processData tasks.
 */
export const selectCodeTaskDataOutputHandlesByTaskID = createSelector(
  selectCodeTaskDataHandlesByTaskID,
  (dataHandlesByTaskID): { [taskId: string]: DataHandleComponentData[] } =>
    filterCodeTaskDataHandlesByTaskId(dataHandlesByTaskID, 'output')
);

export const selectConditionTaskDataInputHandlesByTaskID = createSelector(
  selectConditionTaskHandlesByTaskID,
  (dataHandlesByTaskID): { [taskId: string]: DataHandleComponentData[] } =>
    filterCodeTaskDataHandlesByTaskId(dataHandlesByTaskID, 'input')
);

export const selectConditionTaskLabwareOutputHandlesByTaskID = createSelector(
  selectConditionTaskHandlesByTaskID,
  (dataHandlesByTaskID): { [taskId: string]: DataHandleComponentData[] } =>
    filterCodeTaskDataHandlesByTaskId(dataHandlesByTaskID, 'output')
);

function _labwarePropertiesToDisplayData(
  labwareProperties: LabwareProperties,
  nodeIdToColorIndex: Record<string, number>
): LabwareDataForTask {
  const { id, nickname, labwareType } = labwareProperties;

  const colorIndex = getLabwareColorIndex({
    id,
    labwareType: labwareType,
    nodeIdToColorIndex,
  });

  return {
    id,
    labwareName: nickname ?? labwareType ?? '?',
    colorIndex,
  };
}

export type LabwareDisplayDataByTaskId = {
  [taskId: string]: LabwareDataForTaskByHandlePairId;
};
export const selectLabwareDisplayDataByTaskId = createSelector(
  selectTaskIds,
  selectLabwareIdsForTaskByTaskId,
  selectLabwareProperties,
  selectNodeIdToColorIndex,
  (
    taskIds,
    labwareIdsForTaskByTaskId,
    labwarePropertiesById,
    nodeIdToColorIndex
  ): LabwareDisplayDataByTaskId => {
    return taskIds.reduce<LabwareDisplayDataByTaskId>((acc, taskId) => {
      const handleIdToLabwareId = labwareIdsForTaskByTaskId[taskId];

      if (handleIdToLabwareId === undefined) {
        return acc;
      }

      const labwareDisplayData = Object.entries(handleIdToLabwareId).reduce<{
        [taskId: string]: LabwareDataForTask | null;
      }>((taskAcc, [handlePairId, labwareId]) => {
        if (labwareId === null) {
          return { ...taskAcc, [handlePairId]: null };
        }
        const labwareProperties = labwarePropertiesById[labwareId];
        if (labwareProperties == undefined) {
          logger.error(
            'Missing labware id, cannot derive display data for LabwareDataForTask',
            labwareId
          );
          return { ...taskAcc, [handlePairId]: null };
        }
        const labwareData = _labwarePropertiesToDisplayData(
          labwareProperties,
          nodeIdToColorIndex
        );
        return { ...taskAcc, [handlePairId]: labwareData };
      }, {});

      return { ...acc, [taskId]: labwareDisplayData };
    }, {});
  }
);

export const selectLabwareColorIndexById = createSelector(
  selectNodeIdToColorIndex,
  selectLabwareProperties,
  (nodeIdToColorIndex, labwareProperties): { [labwareId: string]: number } => {
    return Object.values(labwareProperties).reduce<{
      [labwareId: string]: number;
    }>((acc, { id, labwareType }) => {
      return {
        ...acc,
        [id]: getLabwareColorIndex({
          id,
          nodeIdToColorIndex,
          labwareType,
        }),
      };
    }, {});
  }
);

/** true if edge's source and target nodes have the same instrument */
export const selectEdgeHasSameInstrumentsById = createSelector(
  selectLabwareDependencyEdges,
  selectLabwareProperties,
  selectActionTaskPropertiesStrict,
  (
    labwareDependencyEdges,
    labwareProperties,
    taskProperties
  ): { [edgeId: string]: boolean } => {
    return labwareDependencyEdges.reduce<{ [edgeId: string]: boolean }>(
      (acc, edge) => {
        const { id, source, target } = edge;

        const sourceTaskInstrument = taskProperties[source]?.instrumentId;
        const sourceLabwareInstrument =
          labwareProperties[source]?.startLocation;
        const sourceInstrument =
          sourceLabwareInstrument ?? sourceTaskInstrument;

        const targetInstrument = taskProperties[target]?.instrumentId;

        const hasSameInstruments =
          sourceInstrument != undefined &&
          sourceInstrument === targetInstrument;
        return { ...acc, [id]: hasSameInstruments };
      },
      {}
    );
  }
);

export const selectUndoHasHistory = createSelector(
  (state: RootState) => state.undoableState.past,
  (past): boolean => {
    return past.length > 0;
  }
);

export const selectRedoHasFuture = createSelector(
  (state: RootState) => state.undoableState.future,
  (future): boolean => {
    return future.length > 0;
  }
);

export const selectPropertiesSidebarIsOpen = createSelector(
  selectSelectedNodeForPropertiesSidebar,
  selectSelectedTimeConstraintIdForPropertiesSidebar,
  selectUserIsDraggingSelectionBox,
  (
    selectedNode,
    selectedTimeConstraintId,
    userIsDraggingSelectionBox
  ): boolean =>
    (selectedNode !== null || selectedTimeConstraintId !== null) &&
    !userIsDraggingSelectionBox
);

/** to add nodes that will open a sidebar, offset them in X so they'll appear in the new center once the sidebar is open */
export const selectFlowPositionCenterAfterSidebarOpen = createSelector(
  selectPropertiesSidebarIsOpen,
  selectFlowPositionCenter,
  _selectZoom,
  (
    propertiesSidebarIsOpen: boolean,
    flowPositionCenter: XYPosition,
    zoom: number
  ): XYPosition => {
    if (propertiesSidebarIsOpen) {
      // no transition, sidebar will not resize the graph editor
      return flowPositionCenter;
    }

    // anticipate panel opening/closing
    return {
      x: flowPositionCenter.x - SIDEBAR_WIDTH_PX / (2 * zoom),
      y: flowPositionCenter.y,
    };
  }
);

export function taskHasConnectedLabware(
  taskId: string,
  labwareIdsForTaskByTaskId: LabwareByHandleIndexByTaskId
) {
  const handlesById = labwareIdsForTaskByTaskId[taskId] || {};
  if (Object.keys(handlesById).length === 0) return false;
  return Object.values(handlesById).some(Boolean);
}

export type TaskDependencyError =
  | LabwareCorrespondenceType.COMMON_LABWARE
  | 'ONE_TASK_MISSING_LABWARE';

const _getTaskDependencyErrors = (
  task: TaskDependency,
  labwareIdsForTaskByTaskId: LabwareByHandleIndexByTaskId,
  taskProperties: Record<string, AllTaskProperties>
): TaskDependencyError[] => {
  const errors: TaskDependencyError[] = [];

  const isToOrFromCodeTask =
    taskProperties[task.fromTaskId]?.propertiesType === PropertiesType.Code ||
    taskProperties[task.toTaskId]?.propertiesType === PropertiesType.Code;

  const fromIsLabwareLess =
    taskProperties[task.fromTaskId]?.isLabwareLessTask === true;
  const toIsLabwareLess =
    taskProperties[task.toTaskId]?.isLabwareLessTask === true;

  const eitherLabwareLess = fromIsLabwareLess || toIsLabwareLess;

  const taskLabwareCorrespondence = getTaskLabwareCorrespondence(
    task.fromTaskId,
    task.toTaskId,
    labwareIdsForTaskByTaskId
  );

  const fromTaskHasLabware = taskHasConnectedLabware(
    task.fromTaskId,
    labwareIdsForTaskByTaskId
  );

  const toTaskHasLabware = taskHasConnectedLabware(
    task.toTaskId,
    labwareIdsForTaskByTaskId
  );

  if (
    !eitherLabwareLess &&
    taskLabwareCorrespondence === LabwareCorrespondenceType.COMMON_LABWARE &&
    !isToOrFromCodeTask
  ) {
    errors.push(LabwareCorrespondenceType.COMMON_LABWARE);
  }

  if (
    !eitherLabwareLess &&
    !isToOrFromCodeTask &&
    ((!fromTaskHasLabware && toTaskHasLabware) ||
      (fromTaskHasLabware && !toTaskHasLabware))
  ) {
    errors.push(ONE_TASK_MISSING_LABWARE);
  }

  return errors;
};

// TODO(IL, 2025-01-23): can we get this from serialization errors?
export const selectTaskDependenciesErrors = createSelector(
  selectTaskDependencies,
  selectLabwareIdsForTaskByTaskId,
  selectTaskPropertiesStrict,
  (taskDependencies, labwareIdsForTaskByTaskId, taskProperties) => {
    const initialState: Record<string, TaskDependencyError[]> = {};
    const errorsByDependencyId = Object.entries(taskDependencies).reduce(
      (acc, [key, value]) => {
        const errors = _getTaskDependencyErrors(
          value,
          labwareIdsForTaskByTaskId,
          taskProperties
        );
        const hasErrors = errors.length > 0;

        if (!hasErrors) {
          return acc;
        } else {
          return { ...acc, [key]: errors };
        }
      },
      initialState
    );

    if (Object.keys(errorsByDependencyId).length === 0) {
      return null;
    } else {
      return errorsByDependencyId;
    }
  }
);

/** Exclude any labware that lacks an edge */
export const selectConnectedLabwareIds = createSelector(
  selectLabwareIds,
  selectLabwareDependencyEdges,
  (labwareIds: string[], edges: GraphEdges): string[] => {
    return labwareIds.filter((id) => edges.some((edge) => edge.source === id));
  }
);

export const selectConnectedLabwareProperties = createSelector(
  selectConnectedLabwareIds,
  selectLabwareProperties,
  (labwareIds, labware): Record<string, LabwareProperties> =>
    labwareIds.reduce((acc, labwareId) => {
      const labwareProperties = labware[labwareId];
      if (!labwareProperties) return acc;
      return { ...acc, [labwareId]: labwareProperties };
    }, {})
);
